import { type FC, type ReactNode, useEffect, useRef, useState } from 'react';
import classnames from 'classnames';

import { useIntl, defineMessages } from 'dibs-react-intl';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import CircleCheck from 'dibs-icons/exports/legacy/CircleCheck';
import ExclamationCircle from 'dibs-icons/exports/legacy/ExclamationCircle';
import { Spinner } from 'dibs-elements/exports/Spinner';
import { ModalBody } from 'dibs-elements/exports/ModalBody';
import { ModalCloseButton } from 'dibs-elements/exports/ModalCloseButton';
import { ModalContainer } from 'dibs-elements/exports/ModalContainer';
import { Toast } from 'dibs-toaster/exports/Toast';

import styles from './styles/NftProfileImportCollectionModal.scss';

const messages = defineMessages({
    importingTitle: {
        id: 'nftProfile.importCollectionRetryIndicators.importingTitle',
        defaultMessage: 'Importing NFT',
    },
    successTitle: {
        id: 'nftProfile.importCollectionRetryIndicators.successTitle',
        defaultMessage: 'Imported NFT',
    },
    tryAgainTitle: {
        id: 'nftProfile.importCollectionRetryIndicators.tryAgainTitle',
        defaultMessage: 'Import failed',
    },
    importingToast: {
        id: 'nftProfile.importCollectionRetryIndicators.importingToast',
        defaultMessage:
            'Your NFT is importing. This could take a few minutes. Your collection will be updated once the process is complete.',
    },
    tryAgainToast: {
        id: 'nftProfile.importCollectionRetryIndicators.tryAgainToast',
        defaultMessage: 'Your NFT was unable to be imported.',
    },
    successToast: {
        id: 'nftProfile.importCollectionRetryIndicators.successToast',
        defaultMessage: 'Your NFT was imported successfully, and your collection was updated.',
    },
    importing: {
        id: 'nftProfile.importCollectionRetryIndicators.importingModal',
        defaultMessage:
            'Your NFT is importing. This could take a few minutes. You do not need to leave this page open for the transfer to complete successfully. Your collection will be updated once the process is complete.',
    },
    tryAgain: {
        id: 'nftProfile.importCollectionRetryIndicators.tryAgainModal',
        defaultMessage:
            'Your NFT was unable to be imported. Please ensure your NFTs are in an MP4, PNG, JPEG, GIF or GLB format, and try again.',
    },
    success: {
        id: 'nftProfile.importCollectionRetryIndicators.successModal',
        defaultMessage: 'Your NFT was imported successfully, and your collection was updated.',
    },
});

export type NftProfileImportCollectionRetryStatus =
    | (keyof typeof messages & ('importing' | 'tryAgain' | 'success'))
    | null;

const StatusIconMap: FC<{ status: NftProfileImportCollectionRetryStatus; className: string }> = ({
    status,
    className,
}) => {
    switch (status) {
        case 'tryAgain':
            return <ExclamationCircle className={className} />;
        case 'success':
            return <CircleCheck className={className} />;
        case 'importing':
            return <Spinner containerClass={className} black />;
        default:
            return null;
    }
};

export const NftProfileImportCollectionRetryModal: FC<{
    onClose: () => void;
    status: NftProfileImportCollectionRetryStatus;
    isOpen: boolean | null;
    cta?: ReactNode;
}> = ({ onClose, status, isOpen, cta }) => {
    const { formatMessage } = useIntl();
    return status ? (
        <HeadingLevel>
            {Heading => (
                <ModalContainer isOpen={!!isOpen} onClose={onClose}>
                    <ModalCloseButton onClick={onClose} />
                    <Heading className={styles.modalTitle}>
                        <StatusIconMap className={styles.modalIcon} status={status} />
                        {formatMessage(messages[`${status}Title`])}
                    </Heading>
                    <ModalBody className={classnames(styles.modalBody, styles.important)}>
                        <p className={styles.modalBodyContent}>{formatMessage(messages[status])}</p>
                        {cta}
                    </ModalBody>
                </ModalContainer>
            )}
        </HeadingLevel>
    ) : null;
};

export const NftProfileImportCollectionRetryToast: FC<{
    status: NftProfileImportCollectionRetryStatus;
    cta?: ReactNode;
    isModalOpen: boolean | null;
}> = ({ status, isModalOpen, cta }) => {
    const [isOpen, setIsOpen] = useState(isModalOpen);
    const [toastKey, setToastKey] = useState(0);
    const { formatMessage } = useIntl();
    const closedOnRef = useRef<NftProfileImportCollectionRetryStatus>(null);

    useEffect(() => {
        const shouldOpen =
            !isOpen && !!closedOnRef.current && !!status && status !== closedOnRef.current;
        if (isModalOpen === false && shouldOpen) {
            if (closedOnRef.current) {
                // increment key to render new toast
                setToastKey(prev => prev + 1);
            }
            setIsOpen(true);
            closedOnRef.current = null;
        }
    }, [isModalOpen, status, isOpen]);

    if (isModalOpen || !isOpen || !status) {
        return null;
    }
    return (
        <HeadingLevel>
            {Heading => (
                <Toast
                    key={toastKey}
                    type="success"
                    position="bottomRight"
                    isVisible={isOpen}
                    onClose={() => {
                        setIsOpen(false);
                        closedOnRef.current = status;
                    }}
                >
                    <Heading className={styles.toastTitle}>
                        <StatusIconMap className={styles.toastIcon} status={status} />
                        {formatMessage(messages[`${status}Title`])}
                    </Heading>
                    <p className={styles.toastBody}>
                        {formatMessage(messages[`${status}Toast`])} {cta}
                    </p>
                </Toast>
            )}
        </HeadingLevel>
    );
};
